import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

// Os nomes de campo retornados pelo Sankhya são dinâmicos e seguem as chaves
// da entidade (por exemplo, NUMFORM e Parceiro.NOMEPARC).
type FormRow = any;

const FORM_FIELDS = [
  'NUNOTA', 'CabecalhoNota.OBSERVACAOAC', 'CODPARC', 'Parceiro.NOMEPARC', 'CODEMP',
  'Empresa.NOMEFANTASIA', 'NUFORM', 'NUMFORM', 'CODPROD', 'Produto.GPL_PN_ALTERNATIVO',
  'INVOICE', 'NUMNOTA', 'ANEXOS', 'DTFORM', 'CONTROLE', 'TIPCONTEST', 'EMISSOR',
  'PARTNUMBER', 'DTNOTA', 'DTINVOICE', 'SEQUENCIA', 'STATUS', 'SALDO', 'STATUSFORM'
];

const MOVEMENT_FIELDS = [
  'NUFORM', 'SEQMOV', 'CODEMP', 'Empresa.NOMEFANTASIA', 'CODTIPOPER',
  'TipoOperacao.DESCROPER', 'CODPROD', 'Produto.GPL_PN_ALTERNATIVO', 'CODPARC',
  'Parceiro.NOMEPARC', 'TIPMOV', 'CONTROLE', 'SEQUENCIA', 'ItemNota.CODEND',
  'NUNOTA', 'NUMNOTA', 'DTNEG', 'QTDNEG', 'TIPATUALSALDO'
];

@Component({
  selector: 'app-controle-forms',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingIndicatorComponent],
  templateUrl: './controle-forms.component.html',
  styleUrl: './controle-forms.component.css'
})
export class ControleFormsComponent implements OnInit {
  readonly Math = Math;
  readonly pageSize = 15;
  readonly dates = new Intl.DateTimeFormat('pt-BR');

  rows: FormRow[] = [];
  loading = false;
  error = '';
  search = '';
  statusFilter = '';
  page = 1;
  filtersOpen = false;
  selected: FormRow | null = null;
  movements: FormRow[] = [];
  movementsLoading = false;
  movementsError = '';
  filters = { form: '', partNumber: '', parceiro: '', dataInicial: '', dataFinal: '' };

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngOnInit(): void { this.load(); }

  get statuses(): Array<{ value: string; label: string }> {
    const values = [...new Set(this.rows.map(row => String(row.STATUSFORM || row.STATUS || '').trim()).filter(Boolean))].sort();
    return [{ value: '', label: 'Todos' }, ...values.map(value => ({ value, label: value }))];
  }

  get filteredRows(): FormRow[] {
    const term = this.normalize(this.search);
    return this.rows.filter(row => {
      const status = String(row.STATUSFORM || row.STATUS || '').trim();
      const statusMatch = !this.statusFilter || status === this.statusFilter;
      const textMatch = !term || this.normalize([
        row.NUMFORM, row.NUFORM, row.PARTNUMBER, row['Produto.GPL_PN_ALTERNATIVO'],
        row['Parceiro.NOMEPARC'], row.INVOICE, row.NUMNOTA, row.CONTROLE, row.EMISSOR
      ].join(' ')).includes(term);
      const f = this.filters;
      const formMatch = !f.form || String(row.NUMFORM ?? row.NUFORM ?? '').includes(f.form.trim());
      const pnMatch = !f.partNumber || this.normalize(`${row.PARTNUMBER ?? ''} ${row['Produto.GPL_PN_ALTERNATIVO'] ?? ''}`).includes(this.normalize(f.partNumber));
      const partnerMatch = !f.parceiro || this.normalize(row['Parceiro.NOMEPARC']).includes(this.normalize(f.parceiro));
      const date = this.dateValue(row.DTFORM);
      const from = f.dataInicial ? new Date(`${f.dataInicial}T00:00:00`).getTime() : 0;
      const to = f.dataFinal ? new Date(`${f.dataFinal}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER;
      const dateMatch = !(f.dataInicial || f.dataFinal) || Boolean(date && date >= from && date <= to);
      return statusMatch && textMatch && formMatch && pnMatch && partnerMatch && dateMatch;
    });
  }

  get pageRows(): FormRow[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredRows.slice(start, start + this.pageSize);
  }

  get pageCount(): number { return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize)); }
  get totalBalance(): number { return this.filteredRows.reduce((total, row) => total + this.numberValue(row.SALDO), 0); }
  get linkedInvoices(): number { return this.filteredRows.filter(row => row.INVOICE || row.NUMNOTA || row.NUNOTA).length; }

  load(): void {
    this.loading = true;
    this.error = '';
    this.gateway.buscarControleFormsNativo().subscribe({
      next: (response: any) => {
        const body = response?.responseBody ?? response?.serviceResponse?.responseBody ?? response;
        this.rows = (Array.isArray(body?.result) ? body.result : []).map((record: any) => this.toRow(record, FORM_FIELDS));
        this.page = 1;
        this.loading = false;
        if (response?.status && String(response.status) !== '1') this.error = response?.statusMessage || 'Não foi possível consultar os Forms.';
      },
      error: (err: any) => {
        this.loading = false;
        this.error = err?.error?.message ?? err?.error?.detail ?? err?.error?.erro ?? 'Falha ao consultar os Forms. Tente novamente.';
      }
    });
  }

  applyFilters(): void { this.filtersOpen = false; this.page = 1; }
  clearFilters(): void {
    this.filters = { form: '', partNumber: '', parceiro: '', dataInicial: '', dataFinal: '' };
    this.statusFilter = '';
    this.search = '';
    this.page = 1;
  }
  setStatus(value: string): void { this.statusFilter = value; this.page = 1; }
  changePage(offset: number): void { this.page = Math.min(this.pageCount, Math.max(1, this.page + offset)); }
  trackRow(index: number, row: FormRow): string { return String(row.NUFORM ?? `${row.NUMFORM}-${row.SEQUENCIA}-${index}`); }
  trackMovement(index: number, row: FormRow): string { return String(row.SEQMOV ?? `${row.NUNOTA}-${row.SEQUENCIA}-${index}`); }

  openDetails(row: FormRow): void {
    this.selected = row;
    this.movements = [];
    this.movementsError = '';
    this.movementsLoading = true;
    this.gateway.buscarMovimentacoesControleFormsNativo(Number(row.NUFORM ?? 0)).subscribe({
      next: (response: any) => {
        const body = response?.responseBody ?? response?.serviceResponse?.responseBody ?? response;
        this.movements = (Array.isArray(body?.result) ? body.result : []).map((record: any) => this.toRow(record, MOVEMENT_FIELDS));
        this.movementsLoading = false;
        if (response?.status && String(response.status) !== '1') this.movementsError = response?.statusMessage || 'Não foi possível carregar as movimentações.';
      },
      error: (err: any) => {
        this.movementsLoading = false;
        this.movementsError = err?.error?.message ?? err?.error?.detail ?? 'Falha ao carregar as movimentações.';
      }
    });
  }

  closeDetails(): void { this.selected = null; this.movements = []; }

  formatDate(value: unknown): string {
    if (!value) return '—';
    const parsed = this.dateValue(value);
    return parsed ? this.dates.format(new Date(parsed)) : String(value);
  }

  private toRow(record: any, fields: string[]): FormRow {
    if (!record || typeof record !== 'object') return {};
    if (Array.isArray(record)) return Object.fromEntries(fields.map((field, index) => [field, this.unwrap(record[index])]));
    if (Array.isArray(record.field)) return Object.fromEntries(record.field.map((field: any) => [field.fieldName, this.unwrap(field.$ ?? field.value)]));
    return Object.fromEntries(Object.entries(record).map(([key, value]: [string, any]) => [key, this.unwrap(value)]));
  }

  private unwrap(value: any): any { return value && typeof value === 'object' && '$' in value ? value.$ : value; }
  private numberValue(value: unknown): number {
    const raw = String(value ?? '').trim();
    const parsed = Number(raw.includes(',') ? raw.replace(/\./g, '').replace(',', '.') : raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  private dateValue(value: unknown): number {
    if (!value) return 0;
    const text = String(value).trim();
    const br = text.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
    const date = br ? new Date(Number(br[3]), Number(br[2]) - 1, Number(br[1])) : new Date(text);
    const time = date.getTime();
    return Number.isFinite(time) ? time : 0;
  }
  private normalize(value: unknown): string {
    return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().trim();
  }
}
